import * as THREE from "three";
import type { PanoramaSource, PanoramaNode } from "./PanoramaSource.ts";
import type { StreetViewManifest } from "../types.ts";

const MAX_SPHERE_CAMS = 8;

const sphereVert = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = position;
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.0);
}
`;

function buildSphereFrag(n: number): string {
  let src = `varying vec3 vDir;\nuniform int uNumCams;\n`;
  for (let i = 0; i < n; i++) {
    src += `uniform mat3 uRot${i};\nuniform vec2 uFov${i};\nuniform sampler2D uTex${i};\n`;
  }
  src += `void main() {\n  vec3 d = normalize(vDir);\n  vec3 acc = vec3(0.0);\n  float wS = 0.0;\n`;
  for (let i = 0; i < n; i++) {
    src += `  if (${i} < uNumCams) {\n`;
    src += `    vec3 cd = uRot${i} * d;\n`;
    src += `    if (cd.z > 0.01) {\n`;
    src += `      vec2 uv = vec2(cd.x/cd.z/uFov${i}.x*0.5+0.5, cd.y/cd.z/uFov${i}.y*0.5+0.5);\n`;
    src += `      if (uv.x>0.0 && uv.x<1.0 && uv.y>0.0 && uv.y<1.0) {\n`;
    src += `        float w = smoothstep(0.0,0.08,uv.x)*smoothstep(1.0,0.92,uv.x)*smoothstep(0.0,0.08,uv.y)*smoothstep(1.0,0.92,uv.y);\n`;
    src += `        acc += texture2D(uTex${i}, uv).rgb * w;\n`;
    src += `        wS += w;\n`;
    src += `      }\n    }\n  }\n`;
  }
  src += `  gl_FragColor = wS > 0.001 ? vec4(acc/wS,1.0) : vec4(0.05,0.05,0.05,1.0);\n}\n`;
  return src;
}

/**
 * Renders a panorama by blending up to 8 nearest pinhole frames (RGB + per-camera
 * rotation/FOV from RTABmap) onto a back-side sphere.
 *
 * Manifest format: {@link StreetViewManifest} — produced by the RTABmap export
 * pipeline.
 */
export class PinholeBlendSource implements PanoramaSource {
  private nodes_: PanoramaNode[] = [];
  private adjacency = new Map<number, number[]>();
  private positions = new Map<number, THREE.Vector3>();
  private camRotations = new Map<number, THREE.Matrix3>();
  private camFovTans = new Map<number, THREE.Vector2>();

  private material: THREE.ShaderMaterial;
  private texCache = new Map<number, THREE.Texture>();
  private texLoader = new THREE.TextureLoader();
  private dummyTex: THREE.DataTexture;

  constructor(
    manifest: StreetViewManifest,
    private imageBaseUrl: string,
    modelOffset: THREE.Vector3,
    private imageUrlQuery: string = "",
  ) {
    this.dummyTex = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat);
    this.dummyTex.needsUpdate = true;

    const uniforms: Record<string, THREE.IUniform> = { uNumCams: { value: 0 } };
    for (let i = 0; i < MAX_SPHERE_CAMS; i++) {
      uniforms[`uRot${i}`] = { value: new THREE.Matrix3() };
      uniforms[`uFov${i}`] = { value: new THREE.Vector2(1, 1) };
      uniforms[`uTex${i}`] = { value: this.dummyTex };
    }
    this.material = new THREE.ShaderMaterial({
      vertexShader: sphereVert,
      fragmentShader: buildSphereFrag(MAX_SPHERE_CAMS),
      uniforms,
      side: THREE.BackSide,
      depthWrite: false,
    });

    this._processManifest(manifest, modelOffset);
  }

  getNodes(): PanoramaNode[] { return this.nodes_; }
  getNeighbors(nodeId: number): number[] { return this.adjacency.get(nodeId) ?? []; }
  getMaterial(): THREE.Material { return this.material; }
  getSphereYaw(_nodeId: number): number { return 0; } // rotation is per-camera in the shader

  async loadNode(nodeId: number): Promise<void> {
    const nearest = this._kNearest(nodeId, MAX_SPHERE_CAMS);
    await Promise.all(nearest.map(async (id) => {
      if (this.texCache.has(id)) return;
      try {
        const tex = await this.texLoader.loadAsync(`${this.imageBaseUrl}/${id}.jpg${this.imageUrlQuery}`);
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        this.texCache.set(id, tex);
      } catch (err) {
        console.warn(`[PinholeBlendSource] failed to load texture ${id}:`, err);
      }
    }));

    const u = this.material.uniforms;
    u.uNumCams.value = nearest.length;
    for (let i = 0; i < MAX_SPHERE_CAMS; i++) {
      if (i < nearest.length) {
        const id = nearest[i];
        u[`uRot${i}`].value.copy(this.camRotations.get(id)!);
        u[`uFov${i}`].value.copy(this.camFovTans.get(id)!);
        u[`uTex${i}`].value = this.texCache.get(id) ?? this.dummyTex;
      } else {
        u[`uTex${i}`].value = this.dummyTex;
      }
    }
  }

  preloadNeighbors(nodeId: number): void {
    for (const nid of this.adjacency.get(nodeId) ?? []) {
      if (this.texCache.has(nid)) continue;
      this.texLoader.loadAsync(`${this.imageBaseUrl}/${nid}.jpg${this.imageUrlQuery}`).then((tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        this.texCache.set(nid, tex);
      }).catch(() => { /* best effort */ });
    }
  }

  dispose(): void {
    this.material.dispose();
    for (const t of this.texCache.values()) t.dispose();
    this.texCache.clear();
    this.dummyTex.dispose();
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  private _processManifest(manifest: StreetViewManifest, modelOffset: THREE.Vector3): void {
    for (const link of manifest.links) {
      if (!this.adjacency.has(link.from)) this.adjacency.set(link.from, []);
      if (!this.adjacency.has(link.to)) this.adjacency.set(link.to, []);
      this.adjacency.get(link.from)!.push(link.to);
      this.adjacency.get(link.to)!.push(link.from);
    }

    // local_transform rotation: applied ONLY to forward direction, not positions.
    const lt = manifest.local_transform;
    const ltRot = new THREE.Matrix3();
    ltRot.set(
      lt[0], lt[1], lt[2],
      lt[4], lt[5], lt[6],
      lt[8], lt[9], lt[10],
    );
    const applyLtRot = (v: THREE.Vector3) => v.clone().applyMatrix3(ltRot);
    const zupToYup = (v: THREE.Vector3) => new THREE.Vector3(v.x, v.z, -v.y);

    for (const node of manifest.nodes) {
      const p = node.position;
      const pos = new THREE.Vector3(
        p.x + modelOffset.x,
        p.z + modelOffset.y,
        -p.y + modelOffset.z,
      );
      this.positions.set(node.id, pos);

      const r = node.rotation;
      const fwdSlam = new THREE.Vector3(r[2], r[5], r[8]);
      const fwdThree = zupToYup(applyLtRot(fwdSlam)).normalize();
      const initialYaw = Math.atan2(fwdThree.x, fwdThree.z);
      const initialPitch = -Math.asin(Math.max(-1, Math.min(1, fwdThree.y)));

      this.nodes_.push({ id: node.id, position: pos, initialYaw, initialPitch });

      // Per-camera projection uniforms (shader side).
      const rot = new THREE.Matrix3();
      rot.set(
        r[0], r[6], -r[3],
        r[1], r[7], -r[4],
        r[2], r[8], -r[5],
      );
      this.camRotations.set(node.id, rot);
      this.camFovTans.set(node.id, new THREE.Vector2(
        Math.tan(THREE.MathUtils.degToRad(node.fov_h_deg / 2)),
        Math.tan(THREE.MathUtils.degToRad(node.fov_v_deg / 2)),
      ));
    }
  }

  private _kNearest(nodeId: number, k: number): number[] {
    const pos = this.positions.get(nodeId);
    if (!pos) return [];
    return [...this.positions.entries()]
      .map(([id, p]) => ({ id, d2: p.distanceToSquared(pos) }))
      .sort((a, b) => a.d2 - b.d2)
      .slice(0, k)
      .map((e) => e.id);
  }
}
